import React, { useEffect, useState } from 'react';
import {
  computeComposeEmbedGuards,
  docHasTable,
  wouldNextTableRowReject,
  AUTHOR_MAX_WORDS_FOR_RICH_EMBEDS,
} from './authorWordBudget';
import {
  isLogicalTextAlignActive,
  isRtlTextDir,
  textAlignStorageKey,
} from './rtlEditorAlign';
import { countProseWordsFromDoc } from './tiptapExtensions';
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  StrikethroughIcon,
  ListBulletIcon,
  NumberedListIcon,
  ChatBubbleBottomCenterTextIcon,
  CodeBracketSquareIcon,
  PhotoIcon,
  TableCellsIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

function useForceUpdateOnEditor(editor) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!editor) return undefined;
    const bump = () => setTick((n) => n + 1);
    editor.on('transaction', bump);
    editor.on('selectionUpdate', bump);
    return () => {
      editor.off('transaction', bump);
      editor.off('selectionUpdate', bump);
    };
  }, [editor]);
}

function ToolbarBtn({
  onClick,
  active,
  disabled,
  title,
  hint,
  children,
  narrow,
}) {
  const ariaLabel = hint ? `${title}. ${hint}` : title;

  return (
    <span
      className={`group relative inline-flex ${disabled ? 'cursor-not-allowed' : ''}`}
      title={disabled ? ariaLabel : undefined}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={ariaLabel}
        aria-label={ariaLabel}
        aria-pressed={Boolean(active)}
        className={`inline-flex items-center justify-center rounded-md border transition disabled:pointer-events-none disabled:opacity-40 ${
          narrow ? 'px-1.5 py-1.5 min-w-[2rem]' : 'px-2 py-1.5'
        } ${
          active
            ? 'border-violet-300 bg-violet-100 text-violet-950'
            : 'border-transparent bg-white text-slate-700 hover:bg-slate-100 hover:border-slate-200'
        }`}
      >
        {children}
      </button>
      {!disabled ? (
        <span
          role="tooltip"
          className="pointer-events-none absolute left-1/2 top-full z-[70] mt-1.5 max-w-[min(16rem,calc(100vw-2rem))] -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1.5 text-center text-[11px] leading-snug text-white opacity-0 shadow-lg ring-1 ring-white/10 transition-opacity duration-150 group-hover:opacity-100 group-hover:delay-75 group-focus-within:opacity-100"
        >
          <span className="block font-medium">{title}</span>
          {hint ? (
            <span className="mt-0.5 block text-[10px] font-normal text-slate-300">{hint}</span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

function ToolbarDivider() {
  return <span className="mx-0.5 h-6 w-px shrink-0 self-center bg-slate-200" aria-hidden />;
}

export default function AuthoredEditorMenuBar({
  editor,
  onTogglePageVisuals,
  pageVisualsOpen,
  variant = 'full',
  embedToolbar,
  textDir = 'ltr',
}) {
  useForceUpdateOnEditor(editor);

  if (!editor) return null;

  const rtl = isRtlTextDir(textDir);

  const toolbarBaseMax =
    typeof embedToolbar?.baseMaxWords === 'number' ? embedToolbar.baseMaxWords : 240;
  const toolbarPageWords =
    typeof embedToolbar?.pageWordCount === 'number' ? embedToolbar.pageWordCount : 0;
  const toolbarHasImages = Boolean(embedToolbar?.hasPageImages);
  const applyWordCountGate =
    embedToolbar?.applyWordCountGate === undefined ? true : Boolean(embedToolbar.applyWordCountGate);
  const doc = editor.state.doc;
  const embedGuards = computeComposeEmbedGuards({
    pageWordCount: toolbarPageWords,
    hasPageImages: toolbarHasImages,
    doc,
    applyWordCountGate,
  });
  const proseWords = countProseWordsFromDoc(doc);
  const documentHasTable = docHasTable(doc);
  const blockMoreTableRows =
    toolbarBaseMax > 0 &&
    embedGuards.blockTable === false &&
    wouldNextTableRowReject(toolbarBaseMax, doc, proseWords);

  if (variant === 'visualsOnly') {
    return (
      <div className="flex flex-col border-b border-slate-200 bg-slate-50">
        <div
          role="toolbar"
          aria-label="Page tools"
          className="flex flex-wrap items-center gap-1 px-2 py-1.5"
        >
          {typeof onTogglePageVisuals === 'function' ? (
            <ToolbarBtn
              title="Page visuals"
              hint={
                embedGuards.blockPageVisuals
                  ? `Unavailable with table/code block or text over ${AUTHOR_MAX_WORDS_FOR_RICH_EMBEDS} words`
                  : 'Upload, replace, or remove page images'
              }
              disabled={embedGuards.blockPageVisuals}
              active={Boolean(pageVisualsOpen)}
              onClick={() => onTogglePageVisuals()}
            >
              <PhotoIcon className="h-5 w-5" />
            </ToolbarBtn>
          ) : null}
        </div>
      </div>
    );
  }

  const chain = () => editor.chain().focus();
  const inTable = editor.isActive('table');

  return (
    <div className="flex flex-col border-b border-slate-200 bg-slate-50">
    <div
      role="toolbar"
      aria-label="Formatting"
      className="flex flex-wrap items-center gap-1 px-2 py-1.5"
    >
      <ToolbarBtn
        title="Undo"
        hint="Undo last change · ⌘Z / Ctrl+Z"
        onClick={() => chain().undo().run()}
        disabled={!editor.can().undo()}
      >
        <ArrowUturnLeftIcon className="h-5 w-5" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Redo"
        hint="Redo · ⌘⇧Z / Ctrl+Shift+Z"
        onClick={() => chain().redo().run()}
        disabled={!editor.can().redo()}
      >
        <ArrowUturnRightIcon className="h-5 w-5" />
      </ToolbarBtn>

      <ToolbarDivider />

      <ToolbarBtn
        title="Bold"
        hint="⌘B / Ctrl+B"
        active={editor.isActive('bold')}
        onClick={() => chain().toggleBold().run()}
      >
        <BoldIcon className="h-5 w-5" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Italic"
        hint="⌘I / Ctrl+I"
        active={editor.isActive('italic')}
        onClick={() => chain().toggleItalic().run()}
      >
        <ItalicIcon className="h-5 w-5" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Underline"
        hint="Underline selected text"
        active={editor.isActive('underline')}
        onClick={() => chain().toggleUnderline().run()}
      >
        <UnderlineIcon className="h-5 w-5" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Strikethrough"
        hint="⌘⇧S / Ctrl+Shift+S"
        active={editor.isActive('strike')}
        onClick={() => chain().toggleStrike().run()}
      >
        <StrikethroughIcon className="h-5 w-5" />
      </ToolbarBtn>

      <ToolbarDivider />

      <ToolbarBtn
        narrow
        title="Heading 1"
        hint="Largest heading style"
        active={editor.isActive('heading', { level: 1 })}
        onClick={() => chain().toggleHeading({ level: 1 }).run()}
      >
        <span className="text-xs font-bold">H1</span>
      </ToolbarBtn>
      <ToolbarBtn
        narrow
        title="Heading 2"
        hint="Section heading"
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => chain().toggleHeading({ level: 2 }).run()}
      >
        <span className="text-xs font-bold">H2</span>
      </ToolbarBtn>
      <ToolbarBtn
        narrow
        title="Heading 3"
        hint="Subsection heading"
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => chain().toggleHeading({ level: 3 }).run()}
      >
        <span className="text-xs font-bold">H3</span>
      </ToolbarBtn>

      <ToolbarDivider />

      <ToolbarBtn
        title="Bullet list"
        hint="Unordered list · bullet markers"
        active={editor.isActive('bulletList')}
        onClick={() => chain().toggleBulletList().run()}
      >
        <ListBulletIcon className="h-5 w-5" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Numbered list"
        hint="Ordered list · 1, 2, 3…"
        active={editor.isActive('orderedList')}
        onClick={() => chain().toggleOrderedList().run()}
      >
        <NumberedListIcon className="h-5 w-5" />
      </ToolbarBtn>

      <ToolbarDivider />

      <ToolbarBtn
        narrow
        title={rtl ? 'Align start' : 'Align left'}
        hint={
          rtl
            ? 'Paragraph or list aligns to the reading start (right edge)'
            : 'Paragraph or list aligns to the left edge'
        }
        active={isLogicalTextAlignActive(editor, 'start', textDir)}
        onClick={() => chain().setTextAlign(textAlignStorageKey('start', textDir)).run()}
      >
        <span className="text-xs font-semibold tabular-nums">L</span>
      </ToolbarBtn>
      <ToolbarBtn
        narrow
        title="Align center"
        hint="Paragraph or list centers on the page"
        active={isLogicalTextAlignActive(editor, 'center', textDir)}
        onClick={() => chain().setTextAlign(textAlignStorageKey('center', textDir)).run()}
      >
        <span className="text-xs font-semibold tabular-nums">C</span>
      </ToolbarBtn>
      <ToolbarBtn
        narrow
        title={rtl ? 'Align end' : 'Align right'}
        hint={
          rtl
            ? 'Paragraph or list aligns to the reading end (left edge)'
            : 'Paragraph or list aligns to the right edge'
        }
        active={isLogicalTextAlignActive(editor, 'end', textDir)}
        onClick={() => chain().setTextAlign(textAlignStorageKey('end', textDir)).run()}
      >
        <span className="text-xs font-semibold tabular-nums">R</span>
      </ToolbarBtn>

      <ToolbarBtn
        title="Blockquote"
        hint="Indented quotation"
        active={editor.isActive('blockquote')}
        onClick={() => chain().toggleBlockquote().run()}
      >
        <ChatBubbleBottomCenterTextIcon className="h-5 w-5" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Code block"
        hint={
          embedGuards.blockCodeBlock
            ? 'Unavailable with tables, images, or text over ' +
              AUTHOR_MAX_WORDS_FOR_RICH_EMBEDS +
              ' words'
            : 'Monospace block · ⌘⌥C / Ctrl+Alt+C'
        }
        disabled={embedGuards.blockCodeBlock && !editor.isActive('codeBlock')}
        active={editor.isActive('codeBlock')}
        onClick={() => chain().toggleCodeBlock().run()}
      >
        <CodeBracketSquareIcon className="h-5 w-5" />
      </ToolbarBtn>

      <ToolbarDivider />

      {typeof onTogglePageVisuals === 'function' ? (
        <ToolbarBtn
          title="Page visuals"
          hint={
            embedGuards.blockPageVisuals
              ? `Unavailable with table/code block or body text over ${AUTHOR_MAX_WORDS_FOR_RICH_EMBEDS} words`
              : 'Full-width image or half-page slots (1–2 images, stacked vertically)'
          }
          disabled={embedGuards.blockPageVisuals}
          active={Boolean(pageVisualsOpen)}
          onClick={() => onTogglePageVisuals()}
        >
          <PhotoIcon className="h-5 w-5" />
        </ToolbarBtn>
      ) : null}
      <ToolbarBtn
        title="Insert table"
        hint={
          embedGuards.blockTable
            ? 'Unavailable with code blocks, page images, or text over ' +
              AUTHOR_MAX_WORDS_FOR_RICH_EMBEDS +
              ' words'
            : documentHasTable
              ? 'Only one table per page — delete the existing table to insert another'
              : '3×3 with header row'
        }
        disabled={embedGuards.blockTable || documentHasTable}
        onClick={() =>
          chain()
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .run()
        }
      >
        <TableCellsIcon className="h-5 w-5" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Delete table"
        hint="Remove the whole table"
        onClick={() => chain().deleteTable().run()}
        disabled={!editor.can().deleteTable()}
      >
        <TrashIcon className="h-5 w-5" />
      </ToolbarBtn>
    </div>

    {inTable ? (
      <div
        role="toolbar"
        aria-label="Table"
        className="flex flex-wrap items-center gap-1 border-t border-slate-200/90 bg-slate-100/90 px-2 py-1.5"
      >
        <span className="mr-1 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Table
        </span>
        <ToolbarDivider />

        <ToolbarBtn
          narrow
          title="Add column before"
          hint={
            rtl
              ? 'Insert column toward reading start (right of cursor)'
              : 'Insert column left of cursor'
          }
          disabled={!editor.can().addColumnBefore()}
          onClick={() => chain().addColumnBefore().run()}
        >
          <span className="text-[11px] font-semibold">{rtl ? '+→' : '+←'}</span>
        </ToolbarBtn>
        <ToolbarBtn
          narrow
          title="Add column after"
          hint={
            rtl
              ? 'Insert column toward reading end (left of cursor)'
              : 'Insert column right of cursor'
          }
          disabled={!editor.can().addColumnAfter()}
          onClick={() => chain().addColumnAfter().run()}
        >
          <span className="text-[11px] font-semibold">{rtl ? '+←' : '+→'}</span>
        </ToolbarBtn>
        <ToolbarBtn
          narrow
          title="Delete column"
          hint="Remove this column"
          disabled={!editor.can().deleteColumn()}
          onClick={() => chain().deleteColumn().run()}
        >
          <span className="text-[11px] font-semibold">−Col</span>
        </ToolbarBtn>

        <ToolbarDivider />

        <ToolbarBtn
          narrow
          title="Add row above"
          hint="Insert row above current row"
          disabled={!editor.can().addRowBefore() || blockMoreTableRows}
          onClick={() => chain().addRowBefore().run()}
        >
          <span className="text-[11px] font-semibold">+↑</span>
        </ToolbarBtn>
        <ToolbarBtn
          narrow
          title="Add row below"
          hint="Insert row below current row"
          disabled={!editor.can().addRowAfter() || blockMoreTableRows}
          onClick={() => chain().addRowAfter().run()}
        >
          <span className="text-[11px] font-semibold">+↓</span>
        </ToolbarBtn>
        <ToolbarBtn
          narrow
          title="Delete row"
          hint="Remove this row"
          disabled={!editor.can().deleteRow()}
          onClick={() => chain().deleteRow().run()}
        >
          <span className="text-[11px] font-semibold">−Row</span>
        </ToolbarBtn>

        <ToolbarDivider />

        <ToolbarBtn
          narrow
          title="Merge or split cells"
          hint="Merge selection or split merged cell"
          disabled={!editor.can().mergeOrSplit()}
          onClick={() => chain().mergeOrSplit().run()}
        >
          <span className="text-[11px] font-semibold">Cells</span>
        </ToolbarBtn>
        <ToolbarBtn
          narrow
          title="Toggle header row"
          hint="Style top row as header"
          disabled={!editor.can().toggleHeaderRow()}
          onClick={() => chain().toggleHeaderRow().run()}
        >
          <span className="text-[11px] font-semibold">Hdr</span>
        </ToolbarBtn>
      </div>
    ) : null}
    </div>
  );
}
